import type {ReactNode} from 'react';
import {ArrowLeft} from 'lucide-react';

interface DetailHeaderProps {
  /** Back-link label + handler. Omit both on top-level pages (no back link renders). */
  backLabel?: string;
  onBack?: () => void;
  /** Icon element rendered in a tinted square left of the title. */
  icon?: ReactNode;
  title: string;
  /** Inline nodes after the title, like tags or badges. */
  titleExtra?: ReactNode;
  /** Meta line under the title — plain text, or nodes like badges. */
  subtitle?: ReactNode;
  /** Right-aligned actions (button group, status, etc.). */
  actions?: ReactNode;
}

/**
 * Page header: an optional back link, an icon + title + subtitle block, and an
 * actions slot. Shared across enrollment pages so the chrome stays consistent.
 */
export function DetailHeader({backLabel, onBack, icon, title, titleExtra, subtitle, actions}: DetailHeaderProps) {
  return (
    <div className='mb-6'>
      {onBack && (
        <button
          onClick={onBack}
          className='mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline'
        >
          <ArrowLeft className='h-4 w-4' />
          {backLabel}
        </button>
      )}
      <div className='flex flex-wrap items-start gap-x-4 gap-y-3'>
        <div className='flex min-w-0 flex-auto items-center gap-3'>
          {icon && (
            <div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary'>
              {icon}
            </div>
          )}
          <div className='min-w-0'>
            {titleExtra ? (
              <div className='flex flex-wrap items-center gap-2'>
                <h1 className='min-w-0 break-words text-xl font-semibold leading-tight'>{title}</h1>
                {titleExtra}
              </div>
            ) : (
              <h1 className='truncate text-xl font-semibold leading-tight'>{title}</h1>
            )}
            {subtitle && (
              <div className='mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground'>
                {subtitle}
              </div>
            )}
          </div>
        </div>
        {/* Actions stay beside the title + subtitle while both fit on one line, otherwise wrap below, right-aligned. */}
        {actions && <div className='ml-auto max-w-full shrink-0'>{actions}</div>}
      </div>
    </div>
  );
}
